import { Address } from '@liveauctioneers/caterwaul-components/lib/Address/Address';
import {
    getFormattedPhoneNumber,
    getNationalPhoneNumberForm,
} from '@liveauctioneers/caterwaul-components/lib/Forms/PhoneForm/utils/phoneForm';
import Radio from '@liveauctioneers/caterwaul-components/lib/Radio/Radio';
import { FormattedMessage } from '@liveauctioneers/hammer-ui-core/intl';
import { SubtleLink } from '@liveauctioneers/hammer-ui-core/link';
import { LabelMini } from '@liveauctioneers/hammer-ui-core/text';
import styled from 'styled-components';

import { useAppDispatch, useAppSelector } from '@/redux/hooks';
import { getUpdateAccountLoading } from '@/redux/modules/account/updateAccount/updateAccount';
import { updateBidderAddress } from '@/redux/modules/account/updateAccount/updateAccount.actions';
import { isUserDataLoading } from '@/redux/modules/account/user/user.selectors';
import { openAddressModal } from '@/redux/modules/modal';
import { BidderAddress } from '@/types/BidderAddresses';
type Props = {
    address: BidderAddress;
    canEdit: boolean;
    onSelect: (address: BidderAddress) => void;
    selected: boolean;
    selectedId: number;
    showRemoveButton: boolean;
    showSetDefaultButton?: boolean;
};

const BidderAddressListItem = ({
    address,
    canEdit,
    onSelect,
    selected,
    selectedId,
    showRemoveButton,
    showSetDefaultButton,
}: Props) => {
    const dispatch = useAppDispatch();

    const updateAccountLoading = useAppSelector(getUpdateAccountLoading);
    const userDataLoading = useAppSelector(isUserDataLoading);

    const openEditModal = (address: BidderAddress, id: number) => {
        dispatch(openAddressModal({ address, addressID: id }));
    };
    const removeAddress = (address: BidderAddress) => {
        dispatch(updateBidderAddress(address.bidderId, { ...address, enabled: false }, ''));
    };

    const formattedPhoneNumber = getNationalPhoneNumberForm(address.phoneNumber);
    const phoneNumber =
        formattedPhoneNumber.countryCode === 'US' || formattedPhoneNumber.countryCode === 'CA'
            ? getFormattedPhoneNumber(address.phoneNumber)
            : formattedPhoneNumber.phoneNumber;

    const addressComponent = (
        <AddressRow>
            <AddressCol>
                {address.isDefault && (
                    <LabelMini className="mb-2xs inline-block">
                        <FormattedMessage id="default" />
                    </LabelMini>
                )}
                <StyledAddress address={address} />
            </AddressCol>
        </AddressRow>
    );

    return (
        <StyledBidderAddessListItem data-testid="addressListItem">
            {showSetDefaultButton ? (
                <AddressRow>
                    <AddressCol>
                        {address.isDefault && (
                            <LabelMini className="mb-2xs inline-block">
                                <FormattedMessage id="default" />
                            </LabelMini>
                        )}
                        <StyledAddress address={{ ...address, phoneNumber }} />
                    </AddressCol>
                </AddressRow>
            ) : (
                <StyledRadio
                    $selected={selected}
                    disabled={updateAccountLoading || userDataLoading}
                    id={address.addressId}
                    label={addressComponent}
                    onClick={() => {
                        if (!selected) {
                            onSelect(address);
                        }
                    }}
                    selectedName={selectedId.toString()}
                />
            )}
            {canEdit ? (
                <StyledEditRow $radioIndentation={!showSetDefaultButton}>
                    <SubtleLink
                        className="underline"
                        onClick={() => openEditModal(address, address.addressId)}
                    >
                        <FormattedMessage id="addresses.edit" />
                    </SubtleLink>
                    {showRemoveButton && !address.isDefault && (
                        <SubtleLink
                            className="ml-sm underline"
                            onClick={() => removeAddress(address)}
                        >
                            <FormattedMessage id="addresses.remove" />
                        </SubtleLink>
                    )}
                    {!address.isDefault && showSetDefaultButton && (
                        <SubtleLink
                            className="ml-sm underline"
                            onClick={() => onSelect(address)}
                        >
                            <FormattedMessage id="addresses.setDefault" />
                        </SubtleLink>
                    )}
                </StyledEditRow>
            ) : (
                <span></span>
            )}
        </StyledBidderAddessListItem>
    );
};

export default BidderAddressListItem;

const StyledBidderAddessListItem = styled.div`
    margin-bottom: 24px;
`;

const StyledEditRow = styled.div<{ $radioIndentation: boolean }>`
    margin-top: 4px;
    margin-left: ${({ $radioIndentation }) => ($radioIndentation ? 32 : 0)}px;
`;

const AddressRow = styled.div`
    display: flex;
`;

const AddressCol = styled.div`
    display: flex;
    flex-direction: column;
`;

const StyledAddress = styled(Address)`
    margin-bottom: 0;
`;

const StyledRadio = styled(Radio).attrs(() => ({
    radioCircleClassName: 'radioCircleClassName',
}))<{ $selected?: boolean }>`
    padding-left: 26px;

    .radioCircleClassName {
        top: 3px;
        left: 0;
    }
`;
